import './SavingsSummary.scss';

import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { TcoAssessmentSummaryOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, Container, Grid, Header, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals, formatNumber } from '../../common/LocalizationHelper';
import { regions } from '../../common/regions';
import { MatchParam } from '../../common/types';
import { MONTHS_PER_YEAR } from '../../common/uomConversion';
import { TcoContext } from '../../contexts/TcoContext';
import NegativeSavingsAlert from './common/NegativeSavingsAlert';

export interface SummaryStats {
    awsCost: number;
    onPremisesCost: number;
    savings: number;
}

type Props = FlashContextInterface & {
    awsRightSizePolicyName?: string;
    currency: string;
    duration: number;
    region: string;
    summaryTotals?: SummaryStats;
    downloadFn?: () => any;
    downloadLoading?: boolean;
}
const tcoService = new TcoService();
function SavingsSummary(props: Props) {
    const { awsRightSizePolicyName, currency, downloadFn, downloadLoading, duration, region, summaryTotals } = props;

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;

    const { assessment } = useContext(TcoContext);
    const [ loading, setLoading ] = useState(false);
    const [ tcoSummary, setTcoSummary ] = useState<TcoAssessmentSummaryOutput | null>(null);
    const isMounted = useIsMounted();
    
    const loadSummaryData = async () => {
        try {
            setLoading(true);
            // Only load TCO Summary if summary stats are not provided
            if (!summaryTotals && tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const summary = await tcoService.getSummary(tcoAssessmentId);
                if (isMounted()) {
                    setTcoSummary(summary.output);
                }
            }
        } catch (err) {
            if (isMounted()) {
                props.flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_SUMMARY);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadSummaryData();
    
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ tcoAssessmentId, assessment ]);

    const totals: SummaryStats = summaryTotals ?? {
        awsCost: tcoSummary?.summary.aws.adjustedTotal ?? 0,
        onPremisesCost: tcoSummary?.summary.onPremises.adjustedTotal ?? 0,
        savings: tcoSummary?.summary.awsSavingPercent ?? 0,
    };

    const header = (
        <Header variant='h1' actions={downloadFn ?
            (
                <Button iconName='download' loading={downloadLoading} onClick={downloadFn} id='btn-download-summary'>
                    {constants.DOWNLOAD}
                </Button>
            ) : null}>
            {constants.TOTAL_COST_OF_OWNERSHIP_FOR} {duration} {constants.YEARS_IN} {regions[region]?.label} {constants.REGION} <sup>*</sup>
        </Header>
    );

    const summaryDisplay = (currency: string, totalCosts: SummaryStats, duration: number) => {
        const { onPremisesCost, awsCost, savings } = totalCosts;
        const onPremisesMonthly = onPremisesCost / (duration * MONTHS_PER_YEAR);
        const awsMonthly = awsCost / (duration * MONTHS_PER_YEAR);
        return (
            <Grid gridDefinition={[
                {colspan: { default: 4, xs: 4, xxs: 12}},
                {colspan: { default: 4, xs: 4, xxs: 12}},
                {colspan: { default: 4, xs: 4, xxs: 12}}
            ]}>
                <Box textAlign='center'>
                    <div className='metric'>
                        <div>{formatCurrencyNoDecimals(awsCost, currency)}</div>
                        <span>{constants.YOUR_TOTAL_ESTIMATED_AWS_COST} {awsRightSizePolicyName ?
                            (<span>({awsRightSizePolicyName})<sup>**</sup></span>) : null}</span><br/>
                        {formatCurrencyNoDecimals(awsMonthly, currency)} {constants.MONTHLY_AVG}
                    </div>
                </Box>
                <Box textAlign='center'>
                    <div className='metric'>
                        <div>{formatNumber(savings, {style: 'percent', maximumFractionDigits: 0})}</div>
                        <span>{constants.ESTIMATED_SAVINGS_ON_AWS}</span>
                    </div>
                </Box>
                <Box textAlign='center'>
                    <div className='metric'>
                        <div>{formatCurrencyNoDecimals(onPremisesCost, currency)}</div>
                        <span>{constants.YOUR_TOTAL_ESTIMATED_ON_PREM_COST}</span><br/>
                        {formatCurrencyNoDecimals(onPremisesMonthly, currency)} {constants.MONTHLY_AVG}
                    </div>
                </Box>
            </Grid>
        );
    };

    if (!loading && (summaryTotals || tcoSummary)) {
        return (
            <Container data-testid='savings-summary'>
                {header}
                {summaryDisplay(currency ?? tcoSummary?.summary.currency, totals, duration)}
                {tcoSummary?.summary.awsSavingPercent && tcoSummary.summary.awsSavingPercent < 0 ? (
                    <NegativeSavingsAlert serverStatistics={tcoSummary.serverStatistics}/>
                ) : null }
            </Container>
        );
    } else {
        return (<Spinner size='normal' />);
    }
}

export default withFlashContext(SavingsSummary);
